import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "../../services/apiError";
import ScoreboardMethod from "./ScoreboardMethod";
import { formatCalendarDay, formatInstant } from "./lib/format";
import { formatReturnPct, formatSignedUsd } from "./lib/fund";
import { formatUsd, scaled } from "./lib/money";

/** The difference in a sentence: ahead, behind or level. */
function headline({ difference, benchmark_symbol: symbol }) {
  const cents = scaled(difference, 2);
  if (cents === null) return null;
  const held = `the same money held in ${symbol}`;
  if (cents > 0n) return `The account is ${formatUsd(difference)} ahead of ${held}.`;
  if (cents < 0n) return `The account is ${formatUsd(difference.replace(/^-/, ""))} behind ${held}.`;
  return `The account and ${held} are level.`;
}

/** A simple return, always said with what it isn't. */
function returnText(raw) {
  const pct = formatReturnPct(raw);
  return pct === null ? "None — nothing net contributed yet" : `${pct} — not time-weighted`;
}

/** "1 contribution or distribution dated after Oct 1, 2026 isn't counted yet — …" */
function uncountedText(count, through) {
  const day = formatCalendarDay(through);
  if (count === 1) {
    return `1 contribution or distribution dated after ${day} isn't counted yet — it counts from the first snapshot on or after its date.`;
  }
  return `${count} contributions or distributions dated after ${day} aren't counted yet — they count from the first snapshot on or after their dates.`;
}

function Figure({ term, children }) {
  return (
    <div className="flex flex-col">
      <dt className="text-sm text-muted-foreground">{term}</dt>
      <dd className="m-0 font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

Figure.propTypes = {
  term: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/**
 * The scoreboard (spec §5.4, walkthrough 11): the account against the same
 * money held in the benchmark (the shadow) at the latest snapshot — the
 * difference in dollars, each simple return labelled not time-weighted,
 * this tax year's estimated tax and the account after it — and how each is
 * computed. Every figure is GET scoreboard/'s decimal string, formatted as
 * a string. Before the first snapshot (the API's 404) it says what happens
 * next.
 *
 * `state` is useRead's for GET scoreboard/.
 */
function ScoreboardPanel({ state }) {
  const { loading, data: board, error, retry } = state;
  let body;
  if (error) {
    body = (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>Couldn&apos;t load the scoreboard: {apiErrorMessage(error, "it didn't load.")}</span>{" "}
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={retry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (loading && !board) {
    body = (
      <p role="status" className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading the scoreboard…
      </p>
    );
  } else if (!board) {
    body = (
      <p className="m-0 text-sm">
        No snapshot yet. The scoreboard starts at your executor&apos;s first snapshot — the account&apos;s first
        reading, recorded when it syncs the account to an approved target. From then on it compares the account with
        the same money held in the benchmark from the first deposit, and says how each figure is computed.
      </p>
    );
  } else {
    const symbol = board.benchmark_symbol;
    const inception = formatCalendarDay(board.inception);
    const asOf = formatInstant(board.as_of);
    body = (
      <>
        <p className="m-0 text-sm text-muted-foreground">
          {inception ? `Since ${inception} · as of ${asOf}` : `As of ${asOf}`} — snapshot #{board.snapshot}.
        </p>
        <p className="mt-2 mb-0 font-display text-xl font-semibold">{headline(board)}</p>
        <dl className="mt-3 mb-0 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <Figure term="Account">{formatUsd(board.account_value)}</Figure>
          <Figure term={`${symbol} shadow`}>{formatUsd(board.shadow_value)}</Figure>
          <Figure term={`Account − ${symbol} shadow`}>{formatSignedUsd(board.difference)}</Figure>
          <Figure term="Net contributions">
            {formatUsd(board.net_contributions)}{" "}
            <span className="font-normal text-muted-foreground">
              (deposited {formatUsd(board.deposits)}, withdrawn {formatUsd(board.withdrawals)})
            </span>
          </Figure>
          <Figure term="Account's simple return">{returnText(board.account_simple_return_pct)}</Figure>
          <Figure term={`${symbol} shadow's simple return`}>{returnText(board.shadow_simple_return_pct)}</Figure>
          <Figure term={`Estimated tax for ${board.tax_year}`}>{formatUsd(board.estimated_tax)}</Figure>
          <Figure term="Account after estimated tax">{formatUsd(board.account_after_tax)}</Figure>
        </dl>
        <p className="mt-3 mb-0 text-sm">{board.returns_label}</p>
        {board.not_yet_counted > 0 && (
          <p className="mt-1 mb-0 text-sm">{uncountedText(board.not_yet_counted, board.through)}</p>
        )}
        <ScoreboardMethod board={board} />
      </>
    );
  }
  return (
    <section aria-label="Scoreboard" className="rounded-lg border bg-card p-4">
      <h2 className="mt-0 mb-1 text-lg font-semibold">
        Scoreboard{board ? ` — account vs ${board.benchmark_symbol}` : ""}
      </h2>
      {body}
    </section>
  );
}

ScoreboardPanel.propTypes = {
  state: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.object,
    error: PropTypes.object,
    retry: PropTypes.func.isRequired,
  }).isRequired,
};

export default ScoreboardPanel;
